import { useState, useCallback } from 'react';
import { MapPresenter } from '../MapPresenter';
import { MapActiveLayer, WMSLayer } from '../Map.types';
import { MapSettingsService } from '../services/MapSettingsService';
import { UserI } from '../../../components/sitebase/Base.types';
import { UseShareStateReturn } from './useShareState';
import { encodeLayerPayload } from '../shareStateCodec';

export function useMapLayers(
    presenter: MapPresenter,
    remote: MapSettingsService,
    webSettings: UserI['web_settings'],
    share: UseShareStateReturn,
    forecastDate: string
) {
    const [active, setActive] = useState<MapActiveLayer[]>([]);
    const [favourites, setFavourites] = useState<Set<string>>(
        new Set(webSettings.favourite_layers || [])
    );

    const onChangeActiveLayers = useCallback((layers: MapActiveLayer[]) => {
        // uses the ID from the presenter but also append the visibility state of each layer
        const activeLayers = layers.map((layer) => {
            const opacity = layer.layer.getOpacity();
            const opacityValue = Number.isFinite(opacity) ? opacity : 1;
            return encodeLayerPayload({
                id: layer.id,
                visible: layer.layer.getVisible(),
                opacity: opacityValue,
            });
        }).join(",");
        share.updateLayers(activeLayers);
        setActive(layers);
        remote.updateUserWebSettings({ active_layers: activeLayers.length > 0 ? activeLayers.split(",") : [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remote is a service instance and share is a per-render hook result; adding them would make this callback unstable and cascade identity churn through every layer callback
    }, []);

    const onAdd = useCallback((layersToAdd: [wms: string, group: string, layerObj: WMSLayer, visible?: boolean | string, opacity?: number][]) => {
        presenter.removeAllLayers();
        let layers: MapActiveLayer[] = [];
        for (const [wms, group, layer, visible, opacity] of layersToAdd) {
            console.debug("Adding layer", wms, group, layer);
            const visibleValue = typeof visible === "boolean" ? visible : visible !== "hidden";
            layers = presenter.addLayer(wms, group, layer, forecastDate, visibleValue, opacity);
        }
        onChangeActiveLayers(layers);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [forecastDate, onChangeActiveLayers]);

    const onAddOrRemove = useCallback(async (wms: string, group: string, layer: WMSLayer) => {
        console.debug("Adding layer", wms, group, layer);
        const layers = await presenter.addOrRemoveLayer(wms, group, layer, forecastDate);
        onChangeActiveLayers(layers);
        // actionHistory.current.push(`Added layer ${wms} ${group} ${layer.Name}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [forecastDate, onChangeActiveLayers]);

    const onToggleAll = useCallback((visible: boolean) => {
        console.debug("Toggling all layers to", visible);
        const layers = presenter.setAllLayersVisibility(visible);
        onChangeActiveLayers(layers);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [onChangeActiveLayers]);

    const onRemoveAll = useCallback(() => {
        console.debug("Removing all layers");
        const layers = presenter.removeAllLayers();
        setActive(layers);
        onChangeActiveLayers(layers);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [onChangeActiveLayers]);

    const onRemove = useCallback((id: string) => {
        console.debug("Removing layer", id);
        const removeResult = presenter.removeLayer(id);
        const layers = removeResult.newLayers;
        onChangeActiveLayers(layers);
        // actionHistory.current.push(`Removed layer ${id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [onChangeActiveLayers]);

    const onReorder = useCallback((layer: MapActiveLayer, direction: number) => {
        console.debug("Reordering layer", layer, direction);
        const layers = presenter.reorderLayer(layer, direction);
        onChangeActiveLayers(layers);
        // actionHistory.current.push(`Reordered layer ${layer.id} to ${direction}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [onChangeActiveLayers]);

    const onDragReorder = useCallback((layer: MapActiveLayer, newOrder: number) => {
        console.debug("Reordering layer", layer, newOrder);
        const layers = presenter.setLayerOrder(layer, newOrder);
        onChangeActiveLayers(layers);
        // actionHistory.current.push(`Reordered layer ${layer.id} to ${newOrder}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [onChangeActiveLayers]);

    const onSendToTop = useCallback((layer: MapActiveLayer) => {
        console.debug("Sending layer to top", layer);
        const layers = presenter.sendLayerToTop(layer);
        onChangeActiveLayers(layers);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [onChangeActiveLayers]);

    const onToggle = useCallback((id: string, visible?: boolean) => {
        console.debug("Toggling layer", id);
        const layers = presenter.toggleLayer(id, visible);
        onChangeActiveLayers(layers);

        // actionHistory.current.push(`Toggled layer ${id}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [onChangeActiveLayers]);

    const onChangeOpacity = useCallback((layer: MapActiveLayer, opacity: number) => {
        console.debug("Changing opacity", layer, opacity);
        presenter.changeOpacity(layer, opacity);
        onChangeActiveLayers([...presenter.layers]);
        // actionHistory.current.push(`Changed opacity for layer ${layer.id} to ${opacity}`);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- presenter must never be added to a dependency array (see src/apps/map/README.md); adding it risks detaching the presenter from the OL map
    }, [onChangeActiveLayers]);

    const onAddFavourite = useCallback((layerId: string) => {
        const newFavourites = new Set(favourites);
        if (newFavourites.has(layerId)) {
            newFavourites.delete(layerId);
        } else {
            newFavourites.add(layerId);
        }
        setFavourites(newFavourites);
        remote.updateUserWebSettings({ favourite_layers: Array.from(newFavourites) });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- remote is a per-render service instance; adding it would make this callback unstable
    }, [favourites]);

    return {
        active,
        favourites,
        setFavourites,
        onAdd,
        onAddOrRemove,
        onRemove,
        onReorder,
        onDragReorder,
        onSendToTop,
        onToggle,
        onChangeOpacity,
        onToggleAll,
        onRemoveAll,
        onAddFavourite,
    };
}
